/**
 * «Всё видно»: transport to our backend.
 *
 * - PostgREST is served under `/rest/v1` (the reverse proxy strips the prefix),
 *   crm-api under `/api`. Both live on the page's origin.
 * - The session (access + refresh token) lives in localStorage so that all
 *   tabs share it. crm-api rotates refresh tokens and treats the reuse of an
 *   old one as theft (it revokes every token of the user), so refreshes are
 *   serialized across tabs with the Web Locks API, and the stored session is
 *   re-read inside the lock: if another tab has already refreshed, its new
 *   token is used instead of sending ours a second time.
 */
import type { SupabaseClient } from "@supabase/supabase-js";
import { createClient } from "@supabase/supabase-js";
import { HttpError } from "ra-core";

export const SESSION_KEY = "vv.auth.session";
export const API_KEY = "vv";
const REFRESH_LOCK = "vv-refresh";
/** Refresh this long before the access token expires. */
const REFRESH_MARGIN_MS = 60 * 1000;
/** Give up on a hanging refresh call after this long (the session is kept). */
const REFRESH_TIMEOUT_MS = 10 * 1000;

export type Session = {
  access_token: string;
  refresh_token: string;
  /** epoch ms */
  expires_at: number;
  user: { id: string; email: string };
};

export type TokenResponse = {
  access_token: string;
  expires_in: number;
  refresh_token: string;
  user: { id: string; email: string };
};

const storage = (): Storage | null => {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
};

const readSession = (): Session | null => {
  const raw = storage()?.getItem(SESSION_KEY);
  if (!raw) return null;
  try {
    const s = JSON.parse(raw);
    return s && typeof s.access_token === "string" ? (s as Session) : null;
  } catch {
    return null;
  }
};

const isFresh = (s: Session) => s.expires_at - Date.now() > REFRESH_MARGIN_MS;

export const sessionFromResponse = (r: TokenResponse): Session => ({
  access_token: r.access_token,
  refresh_token: r.refresh_token,
  expires_at: Date.now() + r.expires_in * 1000,
  user: r.user,
});

/** JSON body of an error response: `{status, message, code?}` from crm-api. */
const errorFrom = async (res: Response, fallback: string) => {
  let body: any = {};
  try {
    body = await res.json();
  } catch {
    // not JSON
  }
  const message =
    typeof body?.message === "string" && body.message ? body.message : fallback;
  return Object.assign(new HttpError(message, res.status, body), {
    code: body?.code,
  });
};

/**
 * Calls crm-api. JSON in, JSON out (null for 204). Adds the bearer token when
 * there is a session, unless `auth: false`. Rejects with an HttpError carrying
 * the server's Russian message, `status` and `code`.
 */
export const apiFetch = async <T = any>(
  path: string,
  {
    json,
    auth = true,
    ...init
  }: RequestInit & { json?: unknown; auth?: boolean } = {},
): Promise<T> => {
  const headers = new Headers(init.headers);
  if (json !== undefined) {
    headers.set("content-type", "application/json");
    init.body = JSON.stringify(json);
  }
  if (auth) {
    const token = await tokenStore.get();
    if (token) headers.set("Authorization", `Bearer ${token}`);
  }
  const res = await fetch(path, { ...init, headers });
  if (!res.ok) {
    throw await errorFrom(res, `Ошибка сервера (${res.status})`);
  }
  if (res.status === 204) return null as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : null) as T;
};

let inflight: Promise<string | null> | null = null;

/** Runs `fn` holding the cross-tab refresh lock (or in-tab only, without Web Locks). */
const withRefreshLock = <T>(fn: () => Promise<T>): Promise<T> => {
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  if (locks?.request) {
    return locks.request(REFRESH_LOCK, fn) as Promise<T>;
  }
  return fn();
};

/**
 * Refreshes the session inside the lock. Only a 400/401 from crm-api ends the
 * session (the refresh token is dead). A network error, a timeout or a 5xx is
 * transient: the session is kept and the current, maybe stale, access token is
 * returned; the next call tries again.
 */
const doRefresh = (): Promise<string | null> =>
  withRefreshLock(async () => {
    // Re-read inside the lock: another tab may have refreshed meanwhile.
    const current = readSession();
    if (!current) return null;
    if (isFresh(current)) return current.access_token;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS);
    try {
      const res = await fetch("/api/auth/refresh", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ refresh_token: current.refresh_token }),
        signal: controller.signal,
      });
      if (res.status === 400 || res.status === 401) {
        // the refresh token is dead: the session is over
        tokenStore.clear();
        return null;
      }
      if (!res.ok) {
        console.warn(
          `vv: token refresh failed (${res.status}), keeping the session`,
        );
        return current.access_token;
      }
      const session = sessionFromResponse((await res.json()) as TokenResponse);
      tokenStore.save(session);
      return session.access_token;
    } catch (error) {
      console.warn("vv: token refresh failed, keeping the session", error);
      return current.access_token;
    } finally {
      clearTimeout(timer);
    }
  });

const sessionListeners = new Set<() => void>();
const notifySession = () => sessionListeners.forEach((fn) => fn());

/**
 * Calls `fn` when the stored session changes: a refresh or sign-out in this
 * tab or another one. File links built into the page re-render with the new
 * token. Returns the unsubscribe function.
 */
export const subscribeSession = (fn: () => void): (() => void) => {
  sessionListeners.add(fn);
  const onStorage = (e: StorageEvent) => {
    if (e.key === SESSION_KEY || e.key === null) fn();
  };
  if (typeof window !== "undefined") {
    window.addEventListener("storage", onStorage);
  }
  return () => {
    sessionListeners.delete(fn);
    if (typeof window !== "undefined") {
      window.removeEventListener("storage", onStorage);
    }
  };
};

export const tokenStore = {
  read: readSession,
  save(session: Session) {
    storage()?.setItem(SESSION_KEY, JSON.stringify(session));
    notifySession();
  },
  clear() {
    storage()?.removeItem(SESSION_KEY);
    notifySession();
  },
  /** True when there is a session whose access token is (nearly) expired. */
  needsRefresh(): boolean {
    const session = readSession();
    return session != null && !isFresh(session);
  },
  /** The stored access token without refreshing it (for URLs built at render time). */
  peek(): string | null {
    return readSession()?.access_token ?? null;
  },
  /** True when the stored access token has expired (by its own clock). */
  isExpired(): boolean {
    const session = readSession();
    return session != null && session.expires_at <= Date.now();
  },
  /**
   * The access token, refreshed when it expires in less than 60 s. Null when
   * there is no session or crm-api refused the refresh token. When the
   * refresh fails for a transient reason, the stale token is returned.
   */
  async get(): Promise<string | null> {
    const session = readSession();
    if (!session) return null;
    if (isFresh(session)) return session.access_token;
    if (!inflight) {
      inflight = doRefresh().finally(() => {
        inflight = null;
      });
    }
    return inflight;
  },
};

/**
 * supabase-js normally sends the API key as the bearer token when there is no
 * session. "vv" is not a JWT, PostgREST would answer 401, so anonymous
 * requests go without Authorization (PostgREST then uses the anon role).
 */
const fetchWithoutKeyBearer: typeof fetch = (input, init) => {
  const headers = new Headers(init?.headers);
  if (headers.get("Authorization") === `Bearer ${API_KEY}`) {
    headers.delete("Authorization");
  }
  return fetch(input, { ...init, headers });
};

let supabaseClient: SupabaseClient | null = null;

/**
 * supabase-js pointed at our origin: `.from()` / `.rpc()` go to `/rest/v1`.
 * With `accessToken` set, supabase-js does not use its auth module at all.
 */
export const getSupabaseClient = () => {
  if (!supabaseClient) {
    supabaseClient = createClient(location.origin, API_KEY, {
      accessToken: async () => tokenStore.get(),
      global: { fetch: fetchWithoutKeyBearer },
    });
  }
  return supabaseClient;
};

const FILES_PREFIX = "/api/files/";

/**
 * Links to files stored by crm-api need auth; `<img>` and `<a>` cannot send a
 * header, so the current access token goes into `?token=` (and the link is
 * made absolute). Other links are returned unchanged.
 */
export function fileUrl(src: string): string;
export function fileUrl(src: string | undefined | null): string | undefined;
export function fileUrl(src: string | undefined | null) {
  if (!src) return src ?? undefined;
  const absolute = /^[a-z][a-z0-9+.-]*:/i.test(src);
  if (absolute && !src.startsWith(`${location.origin}/`)) return src;
  let url: URL;
  try {
    url = new URL(src, location.origin);
  } catch {
    return src;
  }
  if (url.origin !== location.origin || !url.pathname.startsWith(FILES_PREFIX))
    return src;
  const token = tokenStore.peek();
  if (!token) return src;
  url.searchParams.set("token", token);
  // absolute, so that components that only render "http…" sources keep working
  return url.toString();
}
